import React from 'react';
import { useHasPermission } from 'hooks/usePermissions';
import RouteAccessGate from 'modules/Common/permissions/RouteAccessGate';
import { usePermissionsContext } from 'modules/Common/permissions/PermissionsProvider';

/**
 * Wrapper that gates a route by the User Matrix `read` permission for a
 * given resource. Mirrors the `FeatureGuard` pattern in _routes.jsx:
 *
 *   <PrivateRoute
 *     path="/imports"
 *     allowedTypes={[OrganizationType.Declarant]}
 *     render={(props) => (
 *       <PermissionRoute resourceKey="imports">
 *         <ShipmentsSearchPage {...props} />
 *       </PermissionRoute>
 *     )}
 *   />
 *
 * When `resourceKey` is falsy the wrapper is a no-op (default-allow for
 * routes outside the matrix).
 *
 * When the user lacks `org:{resourceKey}:read` we render "Access Denied" AT
 * THE REQUESTED URL rather than redirecting. A redirect is a history.replace,
 * which unmounts this component — so a denial taken while `/permissions/me`
 * was still settling would outlive its own resolution and strand an entitled
 * user on /not-found. Rendering keeps us mounted, so a late resolution
 * re-renders us and the page appears on its own.
 * See docs/features/denial-never-navigates/00-OVERVIEW.md (N1-N3).
 */
const PermissionRoute = ({ resourceKey, children }) => {
  if (!resourceKey) return children;
  return <RequiredPermissionRoute resourceKey={resourceKey}>{children}</RequiredPermissionRoute>;
};

const RequiredPermissionRoute = ({ resourceKey, children }) => {
  const { status } = usePermissionsContext();
  const permission = `org:${resourceKey}:read`;
  const hasReadAccess = useHasPermission(permission);

  return (
    <RouteAccessGate
      status={status}
      allowed={hasReadAccess}
      requiredPermission={permission}
    >
      {children}
    </RouteAccessGate>
  );
};

export default PermissionRoute;
